import React, { useEffect, useRef, useState } from "react";
import { ComboBox, Item } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListVATRequest } from "../../../../../../services/soap/global/requests/ListVATRequest";

export interface InvoiceVatOption {
  id: string;
  name: string;
  value: string;
}

interface IInvoiceVatDropDownProps {
  selectedVat: string;
  selectedVatValue?: string | number;
  onChange: (data: InvoiceVatOption | null) => void;
  label?: string;
  width?: any;
  isDisabled?: boolean;
}

const InvoiceVatDropDown: React.FC<IInvoiceVatDropDownProps> = ({
  selectedVat,
  selectedVatValue,
  onChange,
  label,
  width = { base: "100%", L: "100%" },
  isDisabled = false,
}) => {
  const { store, globalService } = useDependency();
  const { t } = useTranslation("invoice");
  const resolvedLabel = label ?? t("invoice_transaction.vat_type", { ns: "invoice", defaultValue: "VAT type" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const isUserEditingRef = useRef(false);
  const didResolveByValueRef = useRef(false);

  const list = useAsyncList<InvoiceVatOption>({
    async load({ cursor, filterText }) {
      const offset = cursor ? Number(cursor) : 0;
      const limit = 50;
      try {
        const request = new ListVATRequest(store.Server, store.SessionId, limit, offset);
        const response = await globalService.listVAT(request);
        let items: InvoiceVatOption[] = response?.ROWS?.map((x: any) => x.ITEM) || [];
        if (!Array.isArray(items)) return { items: [] };

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((v) => (v.name ?? "").toLowerCase().includes(ft));
        }

        return {
          items,
          cursor: items.length === limit ? String(offset + limit) : undefined,
        };
      } catch (error) {
        console.error("Error fetching VAT list:", error);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserEditingRef.current) return;
    if (!selectedVat) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedVat));
    const selected = list.items.find((x) => String(x.id) === String(selectedVat));
    setInputValue(selected?.name ?? "");
  }, [selectedVat, list.items]);

  useEffect(() => {
    if (didResolveByValueRef.current) return;
    if (selectedVat) return;
    if (!list.items.length) return;

    const normalizedValue = String(selectedVatValue ?? "").trim();
    if (!normalizedValue) return;

    const parsed = Number(normalizedValue);
    const match = list.items.find((x) => Number(String(x.value ?? "").trim()) === parsed);
    if (!match) return;

    didResolveByValueRef.current = true;
    setSelectedKey(String(match.id));
    setInputValue(match.name ?? "");
    onChange(match);
  }, [selectedVat, selectedVatValue, list.items, onChange]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;
    setInputValue(val);
    list.setFilterText(val);

    if (selectedKey) {
      const selected = list.items.find((x) => String(x.id) === String(selectedKey));
      const selectedLabel = selected?.name ?? "";
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText("");
      onChange(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;
    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String(x.id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? "");
      onChange(selected);
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={resolvedLabel}
      aria-label={resolvedLabel}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      width={width}
      isDisabled={isDisabled}
      onFocus={() => {
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={item.id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default InvoiceVatDropDown;
